import { I18n } from 'aws-amplify'
import { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { requestShippingMethods } from '@/actions/operations/shippingMethod'

import {
  getCurrentUser,
  getOperationsShippingMethods,
  getOperationsShippingMethodsCount,
  getOperationsLoading
} from '@/reducers/selectors'

import {
  Flex,
  Button,
  Cell,
  Text,
  Column,
  Row,
  Slot,
  Table,
  Loader
} from '@/primitives'

import { Pagination } from '@/elements'
import HeaderV2 from '@/components/Operations/Shared/HeaderV2'
import TableFilter from './TableFilter'

import history from '../../../../history'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_MANAGEMENT
} from '@/Util/PermissionUtils'

function ShippingMethodTable({ state, dispatchState, modulePath, backPath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const shippingMethods = getOperationsShippingMethods()
  const shippingMethodsCount = getOperationsShippingMethodsCount()
  const loading = getOperationsLoading()

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_MANAGEMENT
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    if (tableState.page !== state.page || shippingMethods.length === 0) {
      setTableState({ ...state })
      fetchShippingMethods(state.page)
    }
  }, [state.page])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.storageFacilities !== state.storageFacilities
    ) {
      setTableState({ ...state })
      fetchShippingMethods(1)
      changePage(1)
    }
  }, [state.organizations, state.storageFacilities])

  const fetchShippingMethods = page => {
    if (state.organizations.length > 0 && state.storageFacilities.length > 0) {
      dispatch(
        requestShippingMethods({
          filter: {
            organizationId: state.organizations,
            storageFacilityId: state.storageFacilities
          },
          limit: pageSize,
          offset: page - 1
        })
      )
    }
  }

  const changePage = newPage => {
    dispatchState({
      page: newPage
    })
  }

  const onCreate = () => {
    history.push(`${modulePath}/create`)
  }

  const onEdit = id => {
    history.push(`${modulePath}/update/${id}`)
  }

  return (
    <Fragment>
      <HeaderV2
        title={I18n.get('Manage Shipping Methods')}
        buttonText={I18n.get('Add Shipping Method')}
        buttonCallback={onCreate}
        buttonIcon={'add'}
        backPath={backPath}
        showButton={canEdit}
      >
        <TableFilter state={state} dispatchState={dispatchState} />
      </HeaderV2>
      <Loader isLoading={loading}>
        {shippingMethodsCount === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{I18n.get('No entries')}</Text>
          </Flex>
        )}
        {shippingMethodsCount > 0 && (
          <Table aria-colcount='3' className='Operations__Table'>
            <Slot name='head'>
              <Row>
                <Column>{I18n.get('Name')}</Column>
                <Column>{I18n.get('Storage Facility')}</Column>
                <Column></Column>
              </Row>
            </Slot>
            <Slot name='body'>
              {shippingMethods.map(({ id, name, storageFacility }) => (
                <Row key={id}>
                  <Cell columnName={I18n.get('Name')}>{name}</Cell>
                  <Cell columnName={I18n.get('Storage Facility')}>
                    {storageFacility.name}
                  </Cell>
                  <Cell className='Operations__Table__Column__Actions'>
                    {canEdit && (
                      <Button
                        variant='info'
                        size='small'
                        iconBefore='edit'
                        onClick={() => onEdit(id)}
                      />
                    )}
                  </Cell>
                </Row>
              ))}
            </Slot>
          </Table>
        )}
        {shippingMethodsCount > pageSize && (
          <Pagination
            type='simple'
            totalItems={shippingMethodsCount}
            totalPages={totalPages}
            setTotalPages={setTotalPages}
            page={tableState.page}
            setPage={changePage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      </Loader>
    </Fragment>
  )
}

export default ShippingMethodTable
